<script setup lang="ts">
import dayjs from 'dayjs'

import type { Campaign } from '@/types/types'
import { getRowContentCreator, getRowContentScope } from '@/utils/contentScope'

const { getStoresByPermission } = useStore()

const router = useRouter()
const { formatNumber, formatPercentage, formatCurrency } = useNumber()
const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    campaigns: Array<Campaign>
    pagination: {
      currentPage: number
      lastPage: number
      total: number
      pageSize: number
    }
    isLoading: boolean
  }>(),
  {
    pagination: () => ({
      currentPage: 1,
      lastPage: 1,
      total: 0,
      pageSize: 15
    })
  }
)

const emit = defineEmits(['update:changePage'])

const storesWithPermission = computed(() => getStoresByPermission('campaign.read'))

const goToDetails = (campaign: { id: number; isDraft?: boolean }) => {
  if (campaign.isDraft) {
    router.push({
      name: 'CampaignEmailEditPage',
      params: { campaignEmailId: campaign.id }
    })
    return
  }

  router.push({
    name: 'CampaignDetail',
    params: { campaignId: campaign.id },
    query: { type: 'email' }
  })
}

const isLaunched = (date) => dayjs(date) <= dayjs()

type CampaignStatus = 'draft' | 'sent' | 'scheduled'

const computeStatus = (campaign: Campaign): CampaignStatus => {
  if (campaign.isDraft) return 'draft'
  return isLaunched(campaign.sentAt) ? 'sent' : 'scheduled'
}

const statusColor: Record<CampaignStatus, string> = {
  draft: 'gray',
  sent: 'green',
  scheduled: 'yellow'
}

const changePage = (page) => {
  emit('update:changePage', page)
}

const hasCampaignStores = computed(() => {
  return props.campaigns.some((campaign) => campaign.stores?.length > 0)
})

const columns = {
  name: {
    label: t('pages.campaign.campaignPage.table.thead.name')
  },
  status: {
    label: t('pages.campaign.campaignPage.table.thead.status')
  },
  sendAt: {
    label: t('pages.campaign.campaignPage.table.thead.sendAt')
  },
  origin: {
    label: t('contentScope.origin')
  },
  stores: {
    label: t('pages.campaign.campaignPage.table.thead.stores')
  },
  opens: {
    label: t('pages.campaignEmail.listing.thead.opens'),
    headerClass: 'min-w-32',
    numeric: true
  },
  visits: {
    label: t('pages.campaignEmail.listing.thead.visits'),
    headerClass: 'min-w-32',
    numeric: true
  },
  ca: {
    label: t('pages.campaignEmail.listing.thead.grossRevenue'),
    headerClass: 'min-w-32',
    numeric: true
  },
  creator: {
    label: t('contentScope.filters.createdBy')
  }
}

const table = computed(() => {
  let columnsObj = { ...columns }

  if (!hasCampaignStores.value) delete columnsObj.stores

  return columnsObj
})

const rows = computed(() => {
  return props.campaigns.map((campaign) => {
    const status = computeStatus(campaign)
    const row = {
      id: campaign.id,
      isDraft: !!campaign.isDraft,
      data: {
        name: {
          name: campaign.name,
          templateName: campaign.emailContent?.name ?? null
        },
        scope: campaign.scope ?? null,
        creator: campaign.creator ?? null,
        status: {
          key: status,
          label: t(`pages.campaignEmail.status.${status}`),
          color: statusColor[status]
        },
        sendAt: {
          date: campaign.sentAt ? dayjs(campaign.sentAt).format('DD/MM/YYYY') : null,
          hour: campaign.sentAt ? dayjs(campaign.sentAt).format('HH:mm:ss') : null
        },
        stores: campaign.stores,
        opensCount: campaign.statistics?.emailOpenedUnique || '-',
        opensPercentage:
          campaign.statistics?.emailOpenedUnique && campaign.statistics?.emailSent
            ? formatPercentage(
                (campaign.statistics.emailOpenedUnique / campaign.statistics.emailSent) * 100,
                (campaign.statistics.emailOpenedUnique / campaign.statistics.emailSent) * 100 < 1 ? 2 : 1
              )
            : null,
        visitsCount: campaign.statistics?.visitsCount || '-',
        visitsPercentage: campaign.statistics?.visitsPercentage
          ? formatPercentage(campaign.statistics?.visitsPercentage, campaign.statistics?.visitsPercentage < 1 ? 2 : 1)
          : null,
        ca: campaign.statistics?.generatedRevenue ? formatCurrency(campaign.statistics?.generatedRevenue) : '-'
      }
    }

    if (!hasCampaignStores.value) delete row.data.stores

    return row
  })
})
</script>

<template>
  <div>
    <AppTable
      :columns="table"
      :rows="rows"
      :pagination="pagination"
      :is-loading="isLoading"
      row-classes="cursor-pointer"
      @update:change-page="changePage($event)"
      @row-clicked="goToDetails($event)"
    >
      <template #td-name="{ data }">
        <div class="flex min-w-[320px] max-w-[560px] flex-col">
          <p
            class="max-w-[40ch] truncate text-sm font-medium leading-5 text-foreground"
            :title="data.name.name || undefined"
          >
            {{ data.name.name || '-' }}
          </p>
          <p v-if="data.name.templateName" class="mt-1 truncate pl-6 text-sm text-muted-foreground">
            {{ data.name.templateName }}
          </p>
        </div>
      </template>

      <template #th-origin>
        <ContentScopeColumnHeader subject="campaign" />
      </template>
      <template #td-origin="{ data }">
        <ContentScopeBadge :scope="getRowContentScope(data)" />
      </template>

      <template #td-creator="{ data }">
        <ContentScopeCreator :creator="getRowContentCreator(data)" />
      </template>

      <template #td-status="{ data }">
        <BaseChip :text="data.status.label" :color="data.status.color" />
      </template>

      <template #td-sendAt="{ data }">
        <div v-if="data.sendAt.date">
          <p class="text-sm font-medium leading-5 text-foreground">
            {{ data.sendAt.date }}
          </p>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ data.sendAt.hour }}
          </p>
        </div>
        <span v-else class="text-sm text-muted-foreground">-</span>
      </template>

      <template v-if="hasCampaignStores" #td-stores="{ data }">
        <StoreTooltip
          v-if="data.stores.length > 0"
          :stores="data.stores"
          :available-store-ids="storesWithPermission.map((store) => store.id)"
        />
        <span v-else>-</span>
      </template>

      <template #td-opens="{ data }">
        <p>{{ formatNumber(data.opensCount) }}</p>
        <p v-if="data.opensPercentage" class="mt-1 text-xs font-normal text-muted-foreground">
          {{ data.opensPercentage }}
        </p>
      </template>

      <template #td-visits="{ data }">
        <p>{{ formatNumber(data.visitsCount) }}</p>
        <p v-if="data.visitsPercentage" class="mt-1 text-xs font-normal text-muted-foreground">
          {{ data.visitsPercentage }}
        </p>
      </template>
    </AppTable>
  </div>
</template>
